import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { MapPin, Phone, Mail, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Corporate HQ & Offices",
  "Pubs, Bars & Hospitality",
  "Luxury Residential",
  "Concerts & Event Venues",
  "Factories & Industrial",
  "Automotive & Car Wash",
  "Care Homes & Healthcare",
  "Other / Multiple Sites",
];

const FREQUENCIES = ["Daily", "Weekly", "Fortnightly", "Monthly", "One-off Deep Clean"];

const initial = { name: "", company: "", email: "", phone: "", service: SERVICES[0], frequency: FREQUENCIES[0], sqft: "5000", message: "" };

const inputCls =
  "w-full bg-navy-900/70 border border-white/10 rounded-lg px-4 py-3 text-sm text-ice-50 placeholder:text-ice-200/30 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500/40 transition-all duration-300";

export const QuoteForm = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/enquiries`, form);
      setDone(data);
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="relative py-24 sm:py-32 bg-navy-950 grain overflow-hidden"
    >
      <div className="absolute inset-0 spotlight" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.3fr] gap-14">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="eyebrow mb-4">Request a Quote</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ice-50 leading-snug mb-6">
            Tell us about your site.{" "}
            <span className="text-gold-500">We respond within one business day.</span>
          </h2>
          <p className="text-base leading-relaxed text-ice-200/60 mb-10 max-w-md">
            Every enquiry is scoped by a real contracts manager — no automated
            estimates, no obligation.
          </p>
          <div className="space-y-5">
            <div className="flex items-center gap-4" data-testid="contact-address">
              <span className="w-10 h-10 rounded-full bg-gold-500/10 grid place-items-center shrink-0">
                <MapPin className="w-4.5 h-4.5 text-gold-400" />
              </span>
              <p className="text-sm text-ice-200/75">18 The Parade, West Bromwich B71 3NY</p>
            </div>
            <div className="flex items-center gap-4" data-testid="contact-phone">
              <span className="w-10 h-10 rounded-full bg-gold-500/10 grid place-items-center shrink-0">
                <Phone className="w-4.5 h-4.5 text-gold-400" />
              </span>
              <p className="text-sm text-ice-200/75">+44 7491 147099 — answered 24/7 by Goldie, our AI receptionist<br /><span className="text-ice-200/50">Prefer a human? Just ask Goldie to connect you to the team.</span></p>
            </div>
            <div className="flex items-center gap-4" data-testid="contact-email">
              <span className="w-10 h-10 rounded-full bg-gold-500/10 grid place-items-center shrink-0">
                <Mail className="w-4.5 h-4.5 text-gold-400" />
              </span>
              <p className="text-sm text-ice-200/75">hello@goldlinkcleaning.co.uk <span className="text-ice-200/40">(placeholder)</span></p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          {done ? (
            <div
              data-testid="quote-success-panel"
              className="bg-navy-800/60 border border-gold-500/30 rounded-xl p-10 text-center h-full flex flex-col items-center justify-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 12 }}
              >
                <CheckCircle2 className="w-16 h-16 text-gold-400 mb-6" />
              </motion.div>
              <h3 className="font-heading font-bold text-2xl text-ice-50 mb-3">
                Enquiry received.
              </h3>
              <p className="text-sm text-ice-200/60 max-w-sm mb-2">
                Thank you, {done.name.split(" ")[0]}. A contracts manager will be
                in touch within one business day.
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-500/70 mb-8">
                Reference {done.id.slice(-8).toUpperCase()}
              </p>
              <button
                data-testid="quote-send-another-btn"
                onClick={() => { setDone(null); setForm(initial); }}
                className="font-mono text-[11px] uppercase tracking-[0.2em] text-ice-200/60 hover:text-gold-400 border border-white/10 hover:border-gold-500/50 rounded-full px-6 py-3 transition-all duration-300"
              >
                Send Another Enquiry
              </button>
            </div>
          ) : (
            <form
              data-testid="quote-form"
              onSubmit={submit}
              className="bg-navy-800/60 border border-white/10 rounded-xl p-7 sm:p-9 grid sm:grid-cols-2 gap-5"
            >
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-ice-200/50 block mb-2">Contact Name *</label>
                <input data-testid="quote-form-name-input" required value={form.name} onChange={set("name")} placeholder="Jordan Smith" className={inputCls} />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-ice-200/50 block mb-2">Company</label>
                <input data-testid="quote-form-company-input" value={form.company} onChange={set("company")} placeholder="Smith & Co Ltd" className={inputCls} />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-ice-200/50 block mb-2">Email *</label>
                <input data-testid="quote-form-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.co.uk" className={inputCls} />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-ice-200/50 block mb-2">Phone</label>
                <input data-testid="quote-form-phone-input" value={form.phone} onChange={set("phone")} placeholder="+44 ..." className={inputCls} />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-ice-200/50 block mb-2">Service Sector *</label>
                <select data-testid="quote-form-service-select" value={form.service} onChange={set("service")} className={inputCls}>
                  {SERVICES.map((s) => <option key={s} value={s} className="bg-navy-900">{s}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-ice-200/50 block mb-2">Frequency</label>
                <select data-testid="quote-form-frequency-select" value={form.frequency} onChange={set("frequency")} className={inputCls}>
                  {FREQUENCIES.map((f) => <option key={f} value={f} className="bg-navy-900">{f}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-ice-200/50 block mb-2">
                  Approx. Floor Area — <span className="text-gold-400">{Number(form.sqft).toLocaleString()} sq ft</span>
                </label>
                <input
                  data-testid="quote-form-sqft-slider"
                  type="range" min="500" max="100000" step="500"
                  value={form.sqft} onChange={set("sqft")}
                  className="w-full h-1.5 rounded-full bg-navy-700 appearance-none cursor-pointer"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-ice-200/50 block mb-2">Special Requirements</label>
                <textarea data-testid="quote-form-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Access hours, security requirements, current pain points..." className={`${inputCls} resize-none`} />
              </div>
              <div className="sm:col-span-2">
                <button
                  data-testid="quote-form-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-gold-500 text-navy-900 font-heading font-bold text-sm uppercase tracking-widest px-7 py-4 rounded-full hover:bg-gold-400 transition-colors duration-300 disabled:opacity-60"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {loading ? "Sending..." : "Request My Custom Quote"}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
